import moment from 'moment';
import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import Spinner from '@/assets/svg/Spinner';
import {Button, DatePicker, Header} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import DownloadStatementButton from '@/components/Receipt/DownloadStatementButton';
import {merchantService} from '@/services/merchants';
import {merchantTransactionService} from '@/services/merchants/transactionService';
import type {SupportedAssets} from '@/types';
import {AfriexCurrencyCode, AfriexWalletType} from '@/types';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {BusinessTransactionChannel} from '@/types/AfriexBusinessTransactionChannel';
import type {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import MerchantAccountStatement from './MerchantAccountStatement';

// The screen previews the newest rows of the range; Download gets the whole
// range as a PDF the server builds and streams (DownloadStatementButton), so
// the browser never has to page through every transaction.
const STATEMENT_PREVIEW_SIZE = 100;

// The newest rows of the range for the preview, and how many the range holds
// in all. Admin-made rows are relabelled `ADMIN_<type>`, which is how the
// statement table names them.
const loadStatementPreview = async ({
  businessId,
  fromDate,
  toDate,
}: {
  businessId: string;
  fromDate: Date;
  toDate: Date;
}): Promise<{transactions: AfriexBusinessTransaction[]; total: number}> => {
  const res = await merchantTransactionService.getBusinessTransactionList({
    businessId,
    fromDate: fromDate.toISOString(),
    toDate: toDate.toISOString(),
    page: 0,
    limit: STATEMENT_PREVIEW_SIZE,
  });
  const transactions = (res.data ?? []).map((tx) =>
    tx.sourcePaymentMethod?.channel === BusinessTransactionChannel.ADMIN
      ? {...tx, type: `ADMIN_${tx.type}` as BusinessTransactionType}
      : tx,
  );
  return {transactions, total: res.total ?? transactions.length};
};

const MerchantAccountStatementPage: React.FC = () => {
  const params = useParams();
  const userId = params?.userId || '';

  const [user, setUser] = useState<AfriexBusinessUser | null>(null);
  const [business, setBusiness] = useState<AfriexBusiness | null>(null);
  const [walletBalances, setWalletBalances] = useState<
    Partial<Record<SupportedAssets, number>>
  >({});
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [transactionList, setTransactionList] = useState<
    AfriexBusinessTransaction[]
  >([]);
  // How many transactions the loaded range holds, of which the preview shows
  // the newest STATEMENT_PREVIEW_SIZE.
  const [transactionTotal, setTransactionTotal] = useState(0);
  // The range the statement on screen was loaded for: its header and the
  // Download use this, not the pickers, which may have moved since.
  const [statementRange, setStatementRange] = useState({
    fromDate: moment().startOf('day').toDate(),
    toDate: moment().endOf('day').toDate(),
  });
  const [fetchingData, setFetchingData] = useState(false);
  const [fromDate, setFromDate] = useState<Date>(
    moment().startOf('day').toDate(),
  );
  const [toDate, setToDate] = useState<Date>(moment().endOf('day').toDate());

  const fetchInitialData = useCallback(async () => {
    if (!userId) {
      return;
    }
    setIsLoadingDetails(true);
    try {
      const userRes = await merchantService.getBusinessUser(userId);
      if (userRes?.person) {
        setUser(userRes.person);
        if (userRes.person.businessId) {
          const {businessId} = userRes.person;
          const today = {
            fromDate: moment().startOf('day').toDate(),
            toDate: moment().endOf('day').toDate(),
          };
          // The details, balances and today's preview each need only the
          // business id, so they are requested together.
          const [businessRes, balancesRes, preview] = await Promise.all([
            merchantService.getBusinessDetails(businessId),
            merchantService.getBusinessWallet(
              businessId,
              Object.values(AfriexCurrencyCode),
            ),
            loadStatementPreview({businessId, ...today}),
          ]);
          setBusiness(businessRes.business);
          // The endpoint is keyed by wallet type (Withdraw/Deposit/Fee). The
          // account statement reports the Payout/legacy balance, where an
          // untagged credit lands, so read the Withdraw wallet's flat
          // currency -> amount map.
          setWalletBalances(balancesRes?.[AfriexWalletType.Withdraw] ?? {});
          setTransactionList(preview.transactions);
          setTransactionTotal(preview.total);
          setStatementRange(today);
        }
      }
    } catch (error) {
      toast.error(
        'Failed to fetch merchant details: ' +
          getErrorMessage(error, 'Unknown error'),
      );
    } finally {
      setIsLoadingDetails(false);
    }
  }, [userId]);

  useEffect(() => {
    // `fetchInitialData` toasts its own failures, so this only fires if that
    // catch or the finally itself throws. Log rather than swallow silently.
    fetchInitialData().catch(
      logRejection('Failed to load merchant statement data'),
    );
  }, [fetchInitialData]);

  const fetchStatement = async () => {
    if (!business?.id) {
      return;
    }
    setFetchingData(true);
    try {
      const preview = await loadStatementPreview({
        businessId: business.id,
        fromDate,
        toDate,
      });
      setTransactionList(preview.transactions);
      setTransactionTotal(preview.total);
      setStatementRange({fromDate, toDate});
    } catch (error) {
      toast.error(
        'Failed to fetch transactions: ' +
          getErrorMessage(error, 'Unknown error'),
      );
    } finally {
      setFetchingData(false);
    }
  };

  const minDate = new Date(2023, 0, 1);
  const isDateValid =
    fromDate && toDate && moment(toDate).isAfter(moment(fromDate));

  const renderStatement = () => {
    if (fetchingData) {
      return (
        <div className="w-full flex justify-center pt-5 sm:pt-10">
          <Spinner size={8} className="text-cyan-600" />
        </div>
      );
    }

    if (!user || !business) {
      return null;
    }

    return (
      <>
        {transactionTotal > transactionList.length && (
          <p
            className="text-sm text-gray-600 dark:text-gray-400"
            data-cy="merchant-statement-preview-note"
          >
            Showing the latest {transactionList.length} of {transactionTotal}{' '}
            transactions. Download has the full statement.
          </p>
        )}
        <MerchantAccountStatement
          user={user}
          business={business}
          balances={walletBalances}
          transactions={transactionList}
          dates={[statementRange.fromDate, statementRange.toDate]}
          loading={fetchingData}
        />
      </>
    );
  };

  return (
    <div className="mt-10 mb-5 mx-4 md:mx-10">
      <Header
        title="Business Account Statement"
        dataCy="merchant-statement-title"
      />
      <div className="flex justify-center items-center flex-col mt-8">
        {isLoadingDetails ? (
          <Spinner size={8} className="text-cyan-600" />
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex justify-between gap-3 items-center w-680">
              <div className="flex gap-5 items-center">
                {/* `contents` wrappers carry the test hooks without adding a
                    layout box: react-datepicker forwards only a whitelist of
                    props to its input. */}
                <div
                  className="contents"
                  data-cy="merchant-statement-from-date"
                >
                  <DatePicker
                    label="From Date"
                    selectedDate={fromDate}
                    setSelectedDate={(date) => setFromDate(date || new Date())}
                    minDate={minDate}
                    maxDate={new Date()}
                    placeholderText="Select From Date"
                    containerStyle="w-1/4"
                  />
                </div>

                <div className="contents" data-cy="merchant-statement-to-date">
                  <DatePicker
                    label="To Date"
                    selectedDate={toDate}
                    setSelectedDate={(date) => setToDate(date || new Date())}
                    minDate={minDate}
                    maxDate={new Date()}
                    placeholderText="Select To Date"
                    containerStyle="w-1/4"
                  />
                </div>

                <Button
                  onClick={
                    isDateValid
                      ? fetchStatement
                      : () =>
                          toast.error(
                            'From Date can not be higher than To Date',
                          )
                  }
                  colorScheme="cyan"
                  variant="outline"
                  className="self-end"
                  data-cy="merchant-statement-search"
                >
                  Search
                </Button>
              </div>
              <PermissionsProvider
                permission={AfriexAdminPermission.BusinessUserStatementRead}
              >
                <div
                  className="flex flex-col self-end"
                  data-cy="merchant-statement-download"
                >
                  <DownloadStatementButton
                    fileName="merchant_statement"
                    scope={business?.name || business?.id}
                    disabled={!business || fetchingData}
                    download={() =>
                      merchantTransactionService.downloadBusinessUserStatement(
                        userId,
                        {
                          fromDate: statementRange.fromDate.toISOString(),
                          toDate: statementRange.toDate.toISOString(),
                        },
                      )
                    }
                  />
                </div>
              </PermissionsProvider>
            </div>

            {renderStatement()}
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantAccountStatementPage;
